import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, CheckCircle2, Copy, Check, Award, Crown } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import MFld from "./MFld";
import PaymentPill from "./PaymentPill";
import PayPalPanel from "./PayPalPanel";
import ModalHeroRow from "./ModalHeroRow";
import WizardSteps from "./WizardSteps";
import { COUNTRIES, DIAL_CODES, DEFAULT_DIAL_CODE } from "./geo";
import DigitalRentalPass from "../DigitalRentalPass";
import {
  fmtEUR,
  isActiveStatus,
  isRequiredStatus,
  OPTIONAL_FIELDS,
  requiredLabel,
} from "./formatters";

/**
 * BookingModal — public 2-step checkout for instantly-bookable vehicles.
 * Step 1 → collect driver & reservation details.
 * Step 2 → order review + deposit summary before hitting `/public/quick-book`.
 */
export default function BookingModal({ vehicle, form, days, settings, extras, resume, onClose }) {
  const [customer, setCustomer] = useState({
    full_name: resume?.full_name || "",
    email:     resume?.email || "",
    phone:     "",
  });
  // v3.9.7 — Phone split into dial code + local digits. Concatenated
  // as `"+30 12345"` on submit so the server receives a single string.
  const [dialCode, setDialCode] = useState(DEFAULT_DIAL_CODE);
  const [phoneLocal, setPhoneLocal] = useState(
    (resume?.phone || "").split(" ").slice(1).join(" ") || "",
  );
  const [loyalty, setLoyalty] = useState(null);   // v3.9.6 — tier peek
  const [extra, setExtra] = useState(
    resume?.driver_age ? { driver_age: String(resume.driver_age) } : {},
  );
  const [selectedExtras, setSelectedExtras] = useState(
    Object.fromEntries((resume?.addons || []).map((id) => [id, true])),
  );
  const [flight, setFlight]   = useState({ airline_company: "", flight_number: "", flight_time: "" });
  const [ferry, setFerry]     = useState({ company: "", vessel_name: "", arrival_time: "" });
  const [saving, setSaving]   = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [ibanCopied, setIbanCopied] = useState(false);
  // v4.4.8 — 3-step wizard. Step 1 is the fleet grid (closing the modal
  // returns there); 2 = extras, 3 = checkout. `maxStep` keeps visited
  // steps clickable so nothing resets when hopping back and forth.
  const [step, setStep] = useState(resume ? 3 : 2);
  const [maxStep, setMaxStep] = useState(resume ? 3 : 2);
  // v4.3.0 — mandatory terms acceptance before the final submit.
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const termsHtml = settings?.terms_and_conditions || "";

  const cf = settings?.checkout_fields || {};
  // v4.4.7 — field-level validation state for the checkout form.
  const [errors, setErrors] = useState({});
  const configuredMethods = settings?.payment_methods || ["pickup", "card", "bank_transfer"];
  // v4.6.5 — each gateway toggle decides whether its tile shows at all.
  const paymentMethods = useMemo(() => {
    const on = {
      card:          settings?.stripe_enabled !== false,
      paypal:        !!settings?.paypal_enabled,
      viva:          !!settings?.viva_enabled,
      bank_transfer: settings?.bank_transfer_enabled !== false,
      pickup:        settings?.pay_on_pickup_enabled !== false,
    };
    const list = configuredMethods.filter((m) => on[m] !== false && on[m] !== undefined);
    if (on.paypal && !list.includes("paypal")) list.push("paypal");
    if (on.viva && !list.includes("viva")) list.push("viva");
    return list;
  }, [
    JSON.stringify(configuredMethods), settings?.stripe_enabled, settings?.paypal_enabled,
    settings?.viva_enabled, settings?.bank_transfer_enabled, settings?.pay_on_pickup_enabled,
  ]);
  const bank = settings?.bank_details || {};
  const [paymentMethod, setPaymentMethod] = useState(settings?.default_payment_method || paymentMethods[0] || "pickup");
  useEffect(() => {
    if (settings?.default_payment_method) setPaymentMethod(settings.default_payment_method);
  }, [settings?.default_payment_method]);

  // v3.9.6 — Debounced loyalty peek. Once the customer types a plausible
  // email we ping /public/loyalty/peek and surface their Silver/Gold/VIP
  // badge next to the input, so returning guests feel recognised.
  useEffect(() => {
    const email = (customer.email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setLoyalty(null); return; }
    const t = setTimeout(() => {
      api.get(`/public/loyalty/peek?email=${encodeURIComponent(email)}`)
        .then((r) => setLoyalty(r.data?.found ? r.data : null))
        .catch(() => setLoyalty(null));
    }, 400);
    return () => clearTimeout(t);
  }, [customer.email]);

  // v4.4.9 — abandoned-checkout capture. Once the customer is on the
  // checkout step with a plausible email we upsert a recovery intent, so
  // the hourly job can email them a one-click link back to this basket.
  useEffect(() => {
    if (step !== 3 || confirmed) return;
    const email = (customer.email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    const t = setTimeout(() => {
      api.post("/public/checkout-intent", {
        vehicle_id:      vehicle.id,
        pickup_date:     form.pickup_date,
        return_date:     form.return_date,
        pickup_time:     form.pickup_time,
        return_time:     form.return_time,
        pickup_location: form.pickup_loc,
        return_location: form.return_loc,
        email,
        full_name:       customer.full_name.trim(),
        phone:           phoneLocal.trim() ? `${dialCode} ${phoneLocal.trim()}` : "",
        addons:          Object.entries(selectedExtras).filter(([, v]) => v).map(([k]) => k),
        driver_age:      extra.driver_age ? Number(extra.driver_age) : null,
        payment_method:  paymentMethod,
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, confirmed, customer.email, customer.full_name, phoneLocal,
      JSON.stringify(selectedExtras), extra.driver_age, paymentMethod]);

  const isAirport = (form.pickup_loc || "").toLowerCase() === "airport";
  const isPort    = (form.pickup_loc || "").toLowerCase() === "port";
  const dailyRate = Number(vehicle.daily_rate || 0);
  const baseTotal = Math.round(dailyRate * Math.max(days, 1));

  const extrasTotal = useMemo(() => {
    return extras
      .filter((e) => selectedExtras[e.id])
      .reduce((acc, e) => acc + (e.pricing_type === "per_day"
        ? Number(e.price || 0) * Math.max(days, 1)
        : Number(e.price || 0)), 0);
  }, [extras, selectedExtras, days]);

  // v3.9.6 — Tier discount applied on top of the base rate. Loyalty peek
  // returns the exact % (Silver 5, Gold 10, VIP 15) or 0 for Standard.
  const loyaltyDiscountAmount = Math.round(baseTotal * ((loyalty?.discount_pct || 0) / 100));

  // ── v4.4.7 — Driver age policy (mirrors backend pricing._age_surcharges).
  // A Car Group override (> 0) wins over the global Rental Settings value.
  const agePolicy = (key, dflt) => {
    const v = Number(vehicle?.[key] || 0);
    if (v) return v;
    const s = settings?.[key];
    return s === null || s === undefined || s === "" ? dflt : Number(s);
  };
  const minAge    = agePolicy("min_driver_age", 21);
  const maxAge    = agePolicy("max_driver_age", 75);
  const youngThr  = agePolicy("young_driver_threshold", 25);
  const seniorThr = agePolicy("senior_driver_threshold", 72);
  const youngFee  = agePolicy("young_driver_fee_per_day", 0);
  const seniorFee = agePolicy("senior_driver_fee_per_day", 0);
  const ageRaw    = String(extra.driver_age ?? "").trim();
  const driverAge = ageRaw === "" ? null : Number(ageRaw);
  const ageMessage = `Driver must be between ${minAge} and ${maxAge} years old.`;
  const ageInvalid = driverAge !== null
    && (!Number.isFinite(driverAge) || driverAge < minAge || driverAge > maxAge);
  const billableDays = Math.max(days, 1);
  const youngSurcharge = (!ageInvalid && driverAge !== null && driverAge < youngThr)
    ? +(youngFee * billableDays).toFixed(2) : 0;
  const seniorSurcharge = (!ageInvalid && driverAge !== null && driverAge > seniorThr)
    ? +(seniorFee * billableDays).toFixed(2) : 0;
  const ageSurcharge = +(youngSurcharge + seniorSurcharge).toFixed(2);

  const grandTotal = Math.max(
    0, baseTotal + extrasTotal + ageSurcharge - loyaltyDiscountAmount,
  );

  const depositPct = Math.max(0, Math.min(100, Number(settings?.advance_deposit_pct ?? 30)));
  const depositAmount = Math.round(grandTotal * depositPct) / 100;
  const balanceDue    = Math.max(0, +(grandTotal - depositAmount).toFixed(2));
  const dueNow        = depositPct >= 100 ? grandTotal : depositAmount;


  // v4.4.7 — field-level validation: returns a {field: message} map so every
  // offending input can be highlighted at once instead of one toast at a time.
  const ERROR_TESTIDS = {
    full_name: "pb-cust-name",
    email:     "pb-cust-email",
    phone:     "pb-cust-phone",
  };

  const validate = () => {
    const e = {};
    if (!customer.full_name.trim()) e.full_name = "Full name is required.";
    const email = customer.email.trim();
    if (!email) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "Enter a valid email address.";
    if (isRequiredStatus(cf.phone) && !phoneLocal.trim()) e.phone = "Mobile phone is required.";
    for (const f of OPTIONAL_FIELDS) {
      if (isRequiredStatus(cf[f.key]) && !String(extra[f.key] ?? "").trim()) {
        e[f.key] = `${f.label} is required.`;
      }
    }
    if (ageInvalid) e.driver_age = ageMessage;
    return e;
  };

  const focusFirstInvalid = (errs) => {
    const order = ["full_name", "email", "phone", ...OPTIONAL_FIELDS.map((f) => f.key)];
    const key = order.find((k) => errs[k]);
    if (!key) return;
    const testId = ERROR_TESTIDS[key] || `pb-fld-${key}`;
    const el = document.querySelector(`[data-testid="${testId}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => el.focus({ preventScroll: true }), 220);
  };

  const guard = () => {
    const errs = validate();
    setErrors(errs);
    const keys = Object.keys(errs);
    if (keys.length === 0) return true;
    toast.error(
      keys.length === 1 && errs.driver_age
        ? errs.driver_age
        : "Please complete the highlighted fields.",
    );
    goTo(3);
    focusFirstInvalid(errs);
    return false;
  };

  const inputCls = (key) =>
    `pb-input${errors[key] ? " border border-red-500 ring-1 ring-red-500 rounded-lg" : ""}`;

  const clearError = (key) =>
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));

  const goTo = (next) => {
    if (next === 1) { onClose(); return; }
    setStep(next);
    setMaxStep((m) => Math.max(m, next));
  };

  const goToCheckout = () => goTo(3);

  const copyIban = async () => {
    try {
      await navigator.clipboard.writeText(bank.iban || "");
      setIbanCopied(true);
      toast.success("IBAN copied to clipboard");
      setTimeout(() => setIbanCopied(false), 1800);
    } catch {
      toast.error("Copy failed — select the IBAN manually.");
    }
  };

  const submit = async () => {
    const data = await createBooking();
    if (data) setConfirmed(data);
  };

  // v4.6.3 — shared by the standard confirm button and the PayPal panel.
  const createBooking = async () => {
    if (!guard()) return null;
    if (!termsAgreed) {
      toast.error("Please accept the Rental Terms & Conditions to continue.");
      return null;
    }
    setSaving(true);
    try {
      const selectedIds = Object.entries(selectedExtras).filter(([, v]) => v).map(([k]) => k);
      const { data } = await api.post("/public/quick-book", {
        vehicle_id:      vehicle.id,
        pickup_date:     form.pickup_date,
        return_date:     form.return_date,
        pickup_time:     form.pickup_time,
        return_time:     form.return_time,
        pickup_location: form.pickup_loc,
        return_location: form.return_loc,
        full_name:       customer.full_name.trim(),
        email:           customer.email.trim(),
        phone:           phoneLocal.trim() ? `${dialCode} ${phoneLocal.trim()}` : "",
        addons:          selectedIds,
        source:          "internet",
        address:         extra.address || "",
        city:            extra.city || "",
        zip:             extra.zip || "",
        country:         extra.country || "",
        id_passport:     extra.id_passport || "",
        license_number:  extra.license_number || "",
        driver_age:      extra.driver_age ? Number(extra.driver_age) : null,
        notes:           extra.notes || "",
        airline_company: isAirport ? flight.airline_company : "",
        flight_number:   isAirport ? flight.flight_number   : "",
        flight_time:     isAirport ? flight.flight_time     : "",
        ferry_details:   isPort ? { ...ferry } : null,
        payment_method:  isRequestOnly ? null : paymentMethod,
        terms_accepted:  true,
      });
      return data;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  // v4.6.6 — Viva Smart Checkout: persist the booking, create the order
  // server-side, then hand the browser to Viva's hosted page.
  const payWithViva = async () => {
    const booking = await createBooking();
    if (!booking?.id) return;
    try {
      const { data } = await api.post("/payments/viva/create-order", { booking_id: booking.id });
      sessionStorage.setItem("viva_booking_id", booking.id);
      window.location.assign(data.checkout_url);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };

  const isRequestOnly = depositPct <= 0 || paymentMethods.length === 0;
  const activeOptionalFields = OPTIONAL_FIELDS.filter((f) => isActiveStatus(cf[f.key]));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 overflow-y-auto p-4 flex justify-center items-start"
      onClick={onClose}
      data-testid="pb-booking-modal"
    >
      <div
        className="bg-white w-full max-w-4xl rounded-lg shadow-2xl my-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* v4.5.7 — static in-flow navigation header: the bar scrolls with
            the modal content on desktop and never floats over the hero.
            Sticky only below sm, on a fully opaque backing. */}
        {!confirmed && (
          <div className="sm:static sticky top-0 z-20 px-4 sm:px-6 pt-4 pb-3 bg-white border-b border-black/8 sm:border-b-0 rounded-t-lg">
            <WizardSteps current={step} maxStep={maxStep} onSelect={goTo} />
          </div>
        )}
        <header className="px-6 py-4 border-b border-black/8 bg-neutral-50">
          <div className="flex items-start justify-between">
            <div className="flex-1 pr-3">
              <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Complete reservation</p>
              <h2 className="font-display text-2xl mt-1">{vehicle.name}</h2>
              {vehicle.description && (
                <p className="text-sm text-neutral-500 font-normal leading-relaxed mt-1 mb-2" data-testid="pb-modal-description">
                  {vehicle.description}
                </p>
              )}
            </div>
            <button type="button" onClick={onClose} className="p-2 hover:bg-black/5 rounded" data-testid="pb-modal-close">
              <X size={18} />
            </button>
          </div>
          <ModalHeroRow vehicle={vehicle} form={form} days={days} grandTotal={grandTotal} />
        </header>

        <div>
        {confirmed ? (
          <div className="p-8 text-center" data-testid="pb-confirmation">
            <CheckCircle2 size={40} className="text-emerald-600 mx-auto" />
            <h3 className="font-display text-2xl mt-3">Reservation confirmed.</h3>
            <p className="text-sm text-neutral-500 mt-2">Confirmation sent to <strong>{customer.email}</strong>.</p>
            <p className="mt-6 text-xs uppercase tracking-[0.3em] text-neutral-500">Booking reference</p>
            <p className="font-mono text-lg mt-1" data-testid="pb-booking-id">{confirmed.id?.slice(0, 8).toUpperCase()}</p>

            {/* v4.0.5 — Digital Rental Pass rendered inline on confirmation. */}
            <div className="mt-8">
              <DigitalRentalPass
                embedded
                companyName={settings?.company_name}
                booking={{
                  ...confirmed,
                  booking_reference: confirmed.id,
                  full_name:       customer.full_name,
                  customer_email:  customer.email,
                  vehicle_name:    vehicle.name,
                  pickup_date:     form.pickup_date,
                  return_date:     form.return_date,
                  pickup_time:     form.pickup_time,
                  return_time:     form.return_time,
                  pickup_location: form.pickup_loc,
                  return_location: form.return_loc,
                  id_passport:     extra.id_passport || "",
                  license_number:  extra.license_number || "",
                }}
              />
            </div>
            {confirmed.payment_method === "bank_transfer" && confirmed.bank_transfer_snapshot?.iban && (
              <p className="mt-4 text-xs text-neutral-500 max-w-md mx-auto">
                Bank transfer selected — please transfer <strong>{fmtEUR(confirmed.total)}</strong> to
                {" "}<span className="font-mono">{confirmed.bank_transfer_snapshot.iban}</span> using
                reference <strong>{confirmed.id?.slice(0, 8).toUpperCase()}</strong>.
              </p>
            )}
            <div className="mt-8">
              <button
                type="button"
                onClick={onClose}
                className="bg-black text-white text-sm px-6 py-2.5 rounded"
                data-testid="pb-done-btn"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-6 space-y-5" data-testid="pb-step-2" style={{ display: step === 2 ? "block" : "none" }}>
              <h3
                className="uppercase tracking-wider text-xs md:text-sm font-semibold text-neutral-800 border-b border-neutral-200 pb-2 mb-4 mt-2"
                data-testid="pb-section-header-extras"
              >
                Extras &amp; Insurance
              </h3>
              {extras.length > 0 ? (
                <section className="border border-black/10 rounded p-4" data-testid="pb-extras-section">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Add what you need — priced per day or per reservation</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {extras.map((e) => {
                      const checked = !!selectedExtras[e.id];
                      const unit = e.pricing_type === "per_day" ? "/day" : "flat";
                      return (
                        <label
                          key={e.id}
                          className={`flex items-center justify-between px-3 py-2.5 rounded border cursor-pointer transition-colors ${checked ? "border-black bg-neutral-50" : "border-black/10 hover:border-black/30"}`}
                          data-testid={`pb-extra-${e.id}`}
                        >
                          <span className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setSelectedExtras((s) => ({ ...s, [e.id]: !s[e.id] }))}
                              className="w-3.5 h-3.5"
                              data-testid={`pb-extra-cbx-${e.id}`}
                            />
                            {e.name}
                          </span>
                          <span className="text-xs tabular-nums text-neutral-600">
                            +{fmtEUR(e.price)}<span className="text-neutral-400"> {unit}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </section>
              ) : (
                <p className="text-sm text-neutral-500" data-testid="pb-extras-empty">
                  No optional extras are offered for this vehicle — continue to checkout.
                </p>
              )}

            </div>

            <div className="p-6 space-y-5" data-testid="pb-step-3" style={{ display: step === 3 ? "block" : "none" }}>
              <h3
                className="uppercase tracking-wider text-xs md:text-sm font-semibold text-neutral-800 border-b border-neutral-200 pb-2 mb-4 mt-2"
                data-testid="pb-section-header-driver"
              >
                Driver &amp; Reservation Details
              </h3>
              <section className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="pb-customer-section">
                <MFld label={requiredLabel("Full name", isRequiredStatus(cf.full_name))} error={errors.full_name} testId="pb-fld-wrap-full_name">
                  <input className={inputCls("full_name")} value={customer.full_name} onChange={(e) => { clearError("full_name"); setCustomer((c) => ({ ...c, full_name: e.target.value })); }} data-testid="pb-cust-name" />
                </MFld>
                <MFld label={requiredLabel("Email", isRequiredStatus(cf.email))} error={errors.email} testId="pb-fld-wrap-email">
                  <input type="email" className={inputCls("email")} value={customer.email} onChange={(e) => { clearError("email"); setCustomer((c) => ({ ...c, email: e.target.value })); }} data-testid="pb-cust-email" />
                  {loyalty && (
                    <span
                      className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium tracking-wide uppercase px-2 py-0.5 rounded ${
                        loyalty.tier === "VIP"    ? "bg-purple-200 text-purple-900"
                        : loyalty.tier === "Gold" ? "bg-amber-200 text-amber-900"
                        : loyalty.tier === "Silver" ? "bg-slate-200 text-slate-800"
                        : "bg-neutral-200 text-neutral-700"
                      }`}
                      data-testid="pb-loyalty-badge"
                    >
                      {loyalty.tier === "VIP" ? <Crown size={10} /> : <Award size={10} />}
                      Welcome back · {loyalty.tier}
                      {loyalty.discount_pct > 0 && <span className="ml-1">· {loyalty.discount_pct}% off</span>}
                    </span>
                  )}
                </MFld>
                <MFld label={requiredLabel("Mobile phone", isRequiredStatus(cf.phone))} error={errors.phone} testId="pb-fld-wrap-phone">
                  <div
                    className={`flex items-stretch rounded-lg border overflow-hidden focus-within:ring-2 focus-within:ring-neutral-900 ${errors.phone ? "border-red-500 ring-1 ring-red-500" : "border-neutral-300"}`}
                    data-testid="pb-cust-phone-group"
                  >
                    <select
                      value={dialCode}
                      onChange={(e) => setDialCode(e.target.value)}
                      className="w-28 shrink-0 bg-neutral-50 border-r border-neutral-300 px-2 text-sm focus:outline-none tabular-nums"
                      data-testid="pb-cust-dialcode"
                      aria-label="Country calling code"
                    >
                      {DIAL_CODES.map((c) => (
                        <option key={c.iso} value={c.code}>
                          {c.code} ({c.iso})
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      inputMode="numeric"
                      className="flex-1 min-w-0 border-0 px-3 py-2 text-sm focus:outline-none bg-white"
                      value={phoneLocal}
                      onChange={(e) => { clearError("phone"); setPhoneLocal(e.target.value); }}
                      placeholder="e.g. 6912345678"
                      data-testid="pb-cust-phone"
                    />
                  </div>
                </MFld>
              </section>

              {activeOptionalFields.length > 0 && (
                <section className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="pb-dynamic-fields">
                  {activeOptionalFields.map((f) => {
                    // v3.9.7 — Notes (textarea) spans the full row so
                    // the box aligns flush with the inputs above.
                    const wrapperClass = f.type === "textarea" ? "md:col-span-3" : "";
                    const isAge = f.key === "driver_age";
                    const onFieldChange = (e) => {
                      clearError(f.key);
                      setExtra((x) => ({ ...x, [f.key]: e.target.value }));
                    };
                    return (
                      <div key={f.key} className={wrapperClass}>
                        <MFld
                          label={requiredLabel(f.label, isRequiredStatus(cf[f.key]))}
                          error={isAge ? (errors.driver_age || (ageInvalid ? ageMessage : null)) : errors[f.key]}
                          testId={`pb-fld-wrap-${f.key}`}
                        >
                          {f.type === "textarea" ? (
                            <textarea
                              className={`${inputCls(f.key)} min-h-[80px] w-full`}
                              rows={3}
                              value={extra[f.key] || ""}
                              onChange={onFieldChange}
                              data-testid={`pb-fld-${f.key}`}
                            />
                          ) : f.type === "country" ? (
                            <select
                              className={inputCls(f.key)}
                              value={extra[f.key] || ""}
                              onChange={onFieldChange}
                              data-testid={`pb-fld-${f.key}`}
                            >
                              <option value="">Select country…</option>
                              {COUNTRIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={f.type}
                              className={
                                isAge && ageInvalid && !errors.driver_age
                                  ? "pb-input border border-red-500 ring-1 ring-red-500 rounded-lg"
                                  : inputCls(f.key)
                              }
                              {...(isAge ? { min: minAge, max: maxAge, step: 1, inputMode: "numeric", placeholder: `${minAge}–${maxAge}` } : {})}
                              value={extra[f.key] || ""}
                              onChange={onFieldChange}
                              data-testid={`pb-fld-${f.key}`}
                            />
                          )}
                          {isAge && !ageInvalid && !errors.driver_age && (
                            <span className="mt-1 block text-[11px] text-neutral-500" data-testid="pb-age-hint">
                              {youngSurcharge > 0
                                ? `Young driver surcharge applies (under ${youngThr}) · +${fmtEUR(youngSurcharge)}`
                                : seniorSurcharge > 0
                                  ? `Senior driver surcharge applies (over ${seniorThr}) · +${fmtEUR(seniorSurcharge)}`
                                  : `Accepted ages: ${minAge}–${maxAge}`}
                            </span>
                          )}
                        </MFld>
                      </div>
                    );
                  })}
                </section>
              )}

              {isAirport && (
                <section className="border border-black/10 rounded p-4" data-testid="pb-flight-card">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Airport pickup — flight details</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <MFld label="Airline / Carrier"><input className="pb-input" value={flight.airline_company} onChange={(e) => setFlight((f) => ({ ...f, airline_company: e.target.value }))} data-testid="pb-flight-airline" /></MFld>
                    <MFld label="Flight number"><input className="pb-input" value={flight.flight_number} onChange={(e) => setFlight((f) => ({ ...f, flight_number: e.target.value }))} data-testid="pb-flight-number" /></MFld>
                    <MFld label="Arrival time"><input type="time" className="pb-input" value={flight.flight_time} onChange={(e) => setFlight((f) => ({ ...f, flight_time: e.target.value }))} data-testid="pb-flight-time" /></MFld>
                  </div>
                </section>
              )}
              {isPort && (
                <section className="border border-black/10 rounded p-4" data-testid="pb-ferry-card">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Port pickup — ferry details</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <MFld label="Ferry company"><input className="pb-input" value={ferry.company} onChange={(e) => setFerry((f) => ({ ...f, company: e.target.value }))} data-testid="pb-ferry-company" /></MFld>
                    <MFld label="Vessel / ship name"><input className="pb-input" value={ferry.vessel_name} onChange={(e) => setFerry((f) => ({ ...f, vessel_name: e.target.value }))} data-testid="pb-ferry-vessel" /></MFld>
                    <MFld label="Arrival time"><input type="time" className="pb-input" value={ferry.arrival_time} onChange={(e) => setFerry((f) => ({ ...f, arrival_time: e.target.value }))} data-testid="pb-ferry-time" /></MFld>
                  </div>
                </section>
              )}

              {isRequestOnly ? (
                <section
                  className="border border-sky-200 bg-sky-50 rounded p-4 text-sm"
                  data-testid="pb-request-only-callout"
                >
                  <p className="font-medium">No advance payment required.</p>
                  <p className="text-neutral-600 mt-1">
                    Your reservation will be submitted for confirmation. Our team
                    reviews it and gets back to you — the car is not blocked until
                    the request is approved.
                  </p>
                </section>
              ) : paymentMethods.length > 0 && (
                <section className="border border-black/10 rounded p-4" data-testid="pb-payment-section">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
                    Payment method
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {paymentMethods.map((m) => {
                      const gwKey = { card: "stripe", paypal: "paypal", viva: "viva" }[m];
                      return (
                        <PaymentPill
                          key={m}
                          method={m}
                          selected={paymentMethod === m}
                          onSelect={() => setPaymentMethod(m)}
                          labelOverride={gwKey ? settings?.[`${gwKey}_display_label`] : ""}
                          descriptionOverride={gwKey ? settings?.[`${gwKey}_display_description`] : ""}
                        />
                      );
                    })}
                  </div>
                  {paymentMethod === "paypal" && (
                    <PayPalPanel
                      clientId={settings?.paypal_client_id}
                      mode={settings?.paypal_mode}
                      disabled={!termsAgreed}
                      amountLabel={depositPct >= 100 ? fmtEUR(grandTotal) : fmtEUR(dueNow)}
                      ensureBooking={createBooking}
                      onPaid={(booking) => setConfirmed(booking)}
                    />
                  )}
                  {paymentMethod === "viva" && (
                    <p className="text-[11px] text-neutral-500 mt-3" data-testid="pb-viva-note">
                      You will be redirected to Viva Smart Checkout to pay{" "}
                      {depositPct >= 100 ? fmtEUR(grandTotal) : fmtEUR(dueNow)} securely
                      {settings?.viva_mode === "sandbox" ? " (demo mode)" : ""}.
                    </p>
                  )}
                  {paymentMethod === "bank_transfer" && (bank.iban || bank.bank_name) && (
                    <div
                      className="mt-4 bg-neutral-50 border border-black/10 rounded p-4 text-sm"
                      data-testid="pb-bank-details-card"
                    >
                      <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                        Bank transfer details
                      </p>
                      <div className="space-y-1.5 tabular-nums">
                        {bank.bank_name && (
                          <p><span className="text-neutral-500 text-xs">Bank</span><br /><strong>{bank.bank_name}</strong></p>
                        )}
                        {bank.beneficiary && (
                          <p><span className="text-neutral-500 text-xs">Beneficiary</span><br /><strong>{bank.beneficiary}</strong></p>
                        )}
                        {bank.iban && (
                          <div>
                            <span className="text-neutral-500 text-xs">IBAN</span>
                            <div className="flex items-center gap-2 mt-1">
                              <code className="font-mono text-sm bg-white px-2 py-1 rounded border border-black/10 flex-1 break-all" data-testid="pb-bank-iban">{bank.iban}</code>
                              <button
                                type="button"
                                onClick={copyIban}
                                className="text-xs px-3 py-1.5 rounded border border-black/15 hover:border-black inline-flex items-center gap-1"
                                data-testid="pb-copy-iban"
                              >
                                {ibanCopied ? <Check size={12} /> : <Copy size={12} />} {ibanCopied ? "Copied" : "Copy"}
                              </button>
                            </div>
                          </div>
                        )}
                        {bank.swift && (
                          <p><span className="text-neutral-500 text-xs">SWIFT / BIC</span><br /><code className="font-mono">{bank.swift}</code></p>
                        )}
                        {bank.note && (
                          <p className="text-xs text-neutral-500 pt-2 border-t border-black/10 mt-2">{bank.note}</p>
                        )}
                      </div>
                    </div>
                  )}
                </section>
              )}

                {extras.filter((e) => selectedExtras[e.id]).length > 0 && (
                  <section className="border border-black/10 rounded p-4" data-testid="pb-review-extras">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Selected extras</p>
                    <ul className="text-sm space-y-1">
                      {extras.filter((e) => selectedExtras[e.id]).map((e) => {
                        const price = e.pricing_type === "per_day"
                          ? Number(e.price || 0) * Math.max(days, 1)
                          : Number(e.price || 0);
                        return (
                          <li key={e.id} className="flex justify-between" data-testid={`pb-review-extra-${e.id}`}>
                            <span>{e.name}</span>
                            <span className="tabular-nums">{fmtEUR(price)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                )}

                <section className="border border-black rounded-lg p-4 bg-neutral-50" data-testid="pb-review-breakdown">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Payment breakdown</p>
                  <div className="space-y-2 text-sm tabular-nums">
                    <div className="flex justify-between">
                      <span>Rental base rate ({days} × {fmtEUR(dailyRate)})</span>
                      <span data-testid="pb-review-base">{fmtEUR(baseTotal)}</span>
                    </div>
                    {extrasTotal > 0 && (
                      <div className="flex justify-between">
                        <span>Selected extras</span>
                        <span data-testid="pb-review-extras-total">{fmtEUR(extrasTotal)}</span>
                      </div>
                    )}
                    {youngSurcharge > 0 && (
                      <div className="flex justify-between" data-testid="pb-review-yds">
                        <span>Young driver surcharge (under {youngThr} · {billableDays}d)</span>
                        <span>{fmtEUR(youngSurcharge)}</span>
                      </div>
                    )}
                    {seniorSurcharge > 0 && (
                      <div className="flex justify-between" data-testid="pb-review-sds">
                        <span>Senior driver surcharge (over {seniorThr} · {billableDays}d)</span>
                        <span>{fmtEUR(seniorSurcharge)}</span>
                      </div>
                    )}
                    {loyaltyDiscountAmount > 0 && (
                      <div className="flex justify-between text-emerald-700" data-testid="pb-review-loyalty">
                        <span>Loyalty · {loyalty?.tier} ({loyalty?.discount_pct}% off base)</span>
                        <span>−{fmtEUR(loyaltyDiscountAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t border-black/10 pt-2">
                      <span>Grand total</span>
                      <span data-testid="pb-review-grand-total">{fmtEUR(grandTotal)}</span>
                    </div>
                    <div className="flex justify-between text-emerald-700 pt-1">
                      <span>Deposit due now ({depositPct}%)</span>
                      <span data-testid="pb-review-deposit">{fmtEUR(depositAmount)}</span>
                    </div>
                    {balanceDue > 0 && depositPct < 100 && (
                      <div className="flex justify-between text-neutral-600">
                        <span>Balance due at pickup</span>
                        <span data-testid="pb-review-balance">{fmtEUR(balanceDue)}</span>
                      </div>
                    )}
                  </div>
                </section>
            </div>

            <footer className="px-6 py-4 border-t border-black/8 bg-neutral-50 flex justify-between items-center gap-3">
              <label
                className={`text-xs text-neutral-600 flex-1 flex items-start gap-2 cursor-pointer ${step === 3 ? "" : "invisible"}`}
                data-testid="pb-terms-label"
              >
                <input
                  type="checkbox"
                  checked={termsAgreed}
                  onChange={(e) => setTermsAgreed(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 shrink-0"
                  data-testid="pb-terms-checkbox"
                />
                <span>
                  I agree to the Rental Terms &amp; Conditions
                  <span className="text-red-600"> *</span>
                  {" · "}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                    className="underline hover:text-black"
                    data-testid="pb-read-terms-link"
                  >
                    Read Terms
                  </button>
                </span>
              </label>
              {step === 2 ? (
                <button
                  type="button"
                  onClick={goToCheckout}
                  className="bg-black text-white text-sm px-6 py-2.5 rounded inline-flex items-center gap-2 hover:bg-neutral-800 transition-colors"
                  data-testid="pb-continue-btn"
                >
                  Continue to checkout →
                </button>
              ) : (
                <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => goTo(2)}
                  className="text-sm px-4 py-2.5 rounded border border-black/20 text-neutral-700 hover:bg-black hover:text-white hover:border-black transition-colors inline-flex items-center gap-1.5"
                  data-testid="pb-back-to-extras"
                >
                  ← Back to Extras
                </button>
                <button
                  type="button"
                  onClick={isRequestOnly || paymentMethod !== "viva" ? submit : payWithViva}
                  disabled={saving || !termsAgreed}
                  className={`bg-black text-white text-sm px-6 py-2.5 rounded inline-flex items-center gap-2 disabled:opacity-50 ${!isRequestOnly && paymentMethod === "paypal" ? "hidden" : ""}`}
                  data-testid="pb-confirm-btn"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  {isRequestOnly
                    ? "Request Booking"
                    : paymentMethod === "viva"
                    ? `Pay with Viva · ${depositPct >= 100 ? fmtEUR(grandTotal) : fmtEUR(dueNow)}`
                    : depositPct >= 100
                    ? `Confirm & pay · ${fmtEUR(grandTotal)}`
                    : `Confirm & pay deposit · ${fmtEUR(dueNow)}`}
                </button>
                </div>
              )}
            </footer>
          </>
        )}
        </div>
      </div>

      {showTerms && (
        <div
          className="fixed inset-0 z-[70] bg-black/75 overflow-y-auto p-4 flex justify-center items-start"
          onClick={(e) => { e.stopPropagation(); setShowTerms(false); }}
          data-testid="pb-terms-modal"
        >
          <div
            className="bg-white w-full max-w-2xl rounded-lg shadow-2xl my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-6 py-4 border-b border-black/8 flex items-start justify-between">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Agreement</p>
                <h3 className="font-display text-2xl mt-1">Rental Terms &amp; Conditions</h3>
                {settings?.terms_version > 0 && (
                  <p className="text-xs text-neutral-500 mt-1">Revision v{settings.terms_version}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                className="p-2 hover:bg-black/5 rounded"
                data-testid="pb-terms-modal-close"
              >
                <X size={18} />
              </button>
            </header>
            <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
              {termsHtml.trim() ? (
                <div
                  className="terms-rich text-sm leading-relaxed text-neutral-700"
                  data-testid="pb-terms-body"
                  dangerouslySetInnerHTML={{ __html: termsHtml }}
                />
              ) : (
                <p className="text-sm text-neutral-500" data-testid="pb-terms-body-empty">
                  The standard rental terms apply. A full copy of the agreement is attached to your
                  confirmation email and available at pickup.
                </p>
              )}
            </div>
            <footer className="px-6 py-4 border-t border-black/8 bg-neutral-50 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                className="px-4 py-2 text-sm border border-black/15 rounded hover:border-black"
                data-testid="pb-terms-close-btn"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => { setTermsAgreed(true); setShowTerms(false); }}
                className="px-5 py-2 text-sm bg-black text-white rounded hover:opacity-90"
                data-testid="pb-terms-accept-btn"
              >
                I agree
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
